import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Select } from 'antd';

import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';
import { formatEquipmentLabelFromCompanySettings } from '../equipment/equipment.helpers';
import { sortByObjProperty } from '../common/helpers';

const getOption = ({ id, name }) => ({ value: id, label: name });

export default function BoardDropdownFieldSelector({
  dataType,
  divisionIds,
  users,
  value = [],
  onChange,
  options: customOptions = [],
  cardTypeId,
  subDataType,
}) {
  const projects = useSelector((state) => state.projects.projects);
  const customers = useSelector((state) => state.customers.customers);
  const equipment = useSelector((state) => state.equipment.equipment);
  const vendors = useSelector((state) => state.vendors.vendors);
  const buckets = useSelector((state) => state.buckets.buckets);
  const labels = useSelector((state) => state.labels);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);
  const formTemplates = useSelector((state) => state.forms.templates);
  const forms = useSelector((state) => state.forms.lightWeightForms);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const divisions = useSelector((state) => state.settings.divisions);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const data = useMemo(() => {
    const subDataTypeSet = new Set(subDataType);
    const divisionSet = new Set(divisionIds);
    const filterByDivisionIds = ({ divisionIds: divIds, active = true }) => (
      divIds?.some((divisionId) => divisionSet.has(divisionId) && active)
    );

    switch (dataType) {
      case 'Customers':
        return Object.values(customers)
          .filter(filterByDivisionIds)
          .map(getOption);
      case 'Projects':
        return projects
          .filter(({ divisionId, active, projectTypeId }) => divisionSet.has(divisionId)
            && active
            && (!subDataTypeSet.size || subDataTypeSet.has(projectTypeId)))
          .map((project) => ({
            value: project.id,
            label: formatProjectLabelFromCompanySettings({
              name: project.name,
              number: project.number,
              settings,
            }),
          }));
      case 'Users': {
        return users;
      }
      case 'Equipment':
        return equipment
          .filter(filterByDivisionIds)
          .map((eq) => ({
            value: eq.id,
            label: formatEquipmentLabelFromCompanySettings({
              name: eq.name,
              code: eq.code,
              settings,
            }),
          }));
      case 'Vendors':
        return Object.values(vendors)
          .filter(filterByDivisionIds)
          .map(getOption);
      case 'Buckets': {
        return (buckets ?? [])
          .filter((bucket) => bucket.bucketTemplateId === subDataType)
          .map(getOption)
          .sort(sortByObjProperty('label'));
      }
      case 'Labels':
        return labels.map(({ id, title }) => ({ value: id, label: title }));
      case 'ScheduleCategories':
        return scheduleCategories.map(({ id, name }) => ({ value: id, label: name }));
      case 'Divisions':
        return Object.values(divisions || {}).map(({ id, name }) => ({ value: id, label: name }));
      case 'Contacts':
        return Object.values(globalAddressBooks).map(getOption);
      case 'Forms':
        return Object.values(formTemplates).map(getOption);
      case 'Custom':
        return customOptions.map(({ id, name }) => ({ value: id, label: name }));
      default:
        return [];
    }
  }, [
    dataType,
    divisionIds,
    projects,
    customers,
    users,
    equipment,
    vendors,
    divisions,
    buckets,
    labels,
    scheduleCategories,
    boardDetailsMap,
    cardTypeId,
    globalAddressBooks,
    formTemplates,
    forms,
    settings,
    customOptions,
    subDataType,
  ]);

  return (
    <Select
      allowClear
      options={data}
      placeholder={`Select ${dataType}`}
      optionFilterProp="label"
      showSearch
      onChange={onChange}
      value={value}
      mode="multiple"
    />
  );
}

BoardDropdownFieldSelector.propTypes = {
  dataType: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  users: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.array,
  cardTypeId: PropTypes.string,
  subDataType: PropTypes.string,
};

BoardDropdownFieldSelector.defaultProps = {
  dataType: '',
  divisionIds: [],
  users: [],
  value: [],
  options: [],
  cardTypeId: '',
  subDataType: undefined,
};
